// @ts-nocheck
/*
 * Port of views/dashboard/trunks/create.js (origin/main-dev, 253 lines).
 * BYTE-IDENTICAL LOGIC — every state hook, useEffect, validation (name
 * allow-list, auth-type IP vs Registration branch payload, pilot phone
 * validation), and API call preserved. Chrome swap: v1 <Card> → shadcn.
 */
import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { friendlyApiError } from '@/utilities/apiErrorMessage';
import { postApiCall } from '@/service/httpCall';
import { API_TRUNKS_CREATE } from '@/service/url';
import { useNavigate } from 'react-router-dom';
import {
  ROUTES,
  TRUNK_AUTH_TYPE_IP,
  TRUNK_AUTH_TYPE_REGISTRATION,
} from '@/utilities/constant';
import RenderTrunkForm, { pickTrunkPrefixes } from '@/features/trunks/TrunkForm';
import { validatePhone } from '@/utilities/helpers';
import { useAuth, errorCheck } from '@/views/dashboard/auth/auth-utils';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ScrollPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { ArrowLeft, Loader2, Save } from 'lucide-react';
import { Link } from 'react-router-dom';

export default function TrunkCreate() {
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const [name, setName] = useState('');
  const [authType, setAuthType] = useState('');
  const [domain, setDomain] = useState('');
  const [port, setPort] = useState('');
  const [codec, setCodec] = useState('');
  const [pilotNumber, setPilotNumber] = useState('');
  const [operator, setOperator] = useState('');
  const [channels, setChannels] = useState('');
  const [cps, setCps] = useState('');
  const [status, setStatus] = useState('');
  const [server, setServer] = useState('');
  const [prefixes, setPrefixes] = useState(pickTrunkPrefixes());
  const [isInternational, setIsInternational] = useState(null);
  const [countryCodes, setCountryCodes] = useState([]);

  const [authUsername, setAuthUsername] = useState('');
  const [authPassword, setAuthPassword] = useState('');
  const [authServerUrl, setAuthServerUrl] = useState('');
  const [authClientUrl, setAuthClientUrl] = useState('');

  const [ipIdentifyBy, setIpIdentifyBy] = useState('');

  const [showToast, setShowToast] = useState(false);
  const [toastProps, setToastProps] = useState(null);
  const [buttonDisable, setButtonDisable] = useState(false);
  const [error, setError] = useState('');
  // Flip to true on the first failed submit so the form starts rendering
  // inline "X is required" text under every empty required field.
  const [showAllErrors, setShowAllErrors] = useState(false);

  const handleChange = (e) => {
    const value = e.target.value;
    const onlyValid = value.replace(/[^a-zA-Z0-9_]/g, '');
    if (value !== onlyValid) {
      setError('Only letters, numbers, and underscore (_) are allowed.');
    } else {
      setError('');
    }
    setName(onlyValid);
  };

  useEffect(() => {
    checkAuth(setToastProps, setShowToast);
  }, []);

  function validateForm() {
    let valid = true;

    if (!name.trim()) {
      valid = false;
      toast.error('Name required');
    } else if (!authType) {
      valid = false;
      toast.error('Authentication required');
    } else if (!domain.trim()) {
      valid = false;
      toast.error('Domain required');
    } else if (isNaN(port) || port < 1024 || port > 65535) {
      valid = false;
      toast.error('Invalid port value');
    } else if (authType == TRUNK_AUTH_TYPE_IP && !ipIdentifyBy.trim()) {
      valid = false;
      toast.error('Identify By required');
    } else if (authType == TRUNK_AUTH_TYPE_REGISTRATION) {
      if (!authUsername.trim()) {
        valid = false;
        toast.error('Authentication username required');
      } else if (!authPassword.trim()) {
        valid = false;
        toast.error('Authentication password required');
      } else if (!authClientUrl.trim()) {
        valid = false;
        toast.error('Authentication client url required');
      } else if (!authServerUrl.trim()) {
        valid = false;
        toast.error('Authentication server url required');
      }
    } else if (!codec) {
      valid = false;
      toast.error('Codec required');
    } else if (isNaN(channels) || channels < 0 || channels > 10000) {
      valid = false;
      toast.error('Invalid channel value. Maximum allowed is 10000');
    } else if (isNaN(cps) || cps < 0 || cps > 1000) {
      valid = false;
      toast.error('Invalid calls per sec value. Maximum allowed is 1000');
    } else if (!server) {
      valid = false;
      toast.error('Server required');
    }

    if (isInternational === null || isInternational === undefined) {
      valid = false;
      toast.error('Trunk type required');
    } else if (!countryCodes.length) {
      valid = false;
      toast.error('Select at least one country code');
    }

    if (!validatePhone(pilotNumber)) {
      valid = false;
      toast.error('Invalid Pilot number format');
    }

    return valid;
  }

  async function submitForm(e) {
    e.preventDefault();
    setButtonDisable(true);

    if (!validateForm()) {
      setShowAllErrors(true);
      setButtonDisable(false);
      return;
    }

    let params = {
      name,
      auth_type: authType,
      domain,
      port,
      codec,
      pilot_number: pilotNumber,
      operator,
      channels,
      cps,
      server,
      is_international: isInternational,
      country_codes: countryCodes,
      ...prefixes,
    };
    if (authType == TRUNK_AUTH_TYPE_REGISTRATION) {
      const authTypeRegistration = {
        username: authUsername,
        password: authPassword,
        client_url: authClientUrl,
        server_url: authServerUrl,
      };
      params = { ...params, auth_type_registration: authTypeRegistration };
    } else if (authType == TRUNK_AUTH_TYPE_IP) {
      params = { ...params, auth_type_identify_by: ipIdentifyBy };
    }

    postApiCall(API_TRUNKS_CREATE, params)
      .then(onTrunkCreateSuccess)
      .catch(onTrunkCreateError);
  }

  function onTrunkCreateSuccess() {
    toast.success('Trunk created successfully');
    setTimeout(() => {
      navigate(`/${ROUTES.TRUNKS.LIST}`);
    }, 1500);
  }

  function onTrunkCreateError(err) {
    if (typeof err !== 'object' && err.includes('TokenExpiredError')) {
      errorCheck(err, navigate, setToastProps, setShowToast);
    } else {
      setButtonDisable(false);
      toast.error(friendlyApiError(err) || 'Failed to create trunk');
    }
  }

  return (
    <ScrollPage>
      <PageHeader
        title="New Trunk"
        description="Register a SIP trunk / carrier with auth, codec, and channel settings."
        actions={
          <Button asChild variant="outline">
            <Link to={`/${ROUTES.TRUNKS.LIST}`}>
              <ArrowLeft className="h-4 w-4" />
              Back
            </Link>
          </Button>
        }
      />
      <Card className="p-4">
        <form onSubmit={submitForm}>
          <RenderTrunkForm
                  name={name}
                  setName={setName}
                  authType={authType}
                  setAuthType={setAuthType}
                  domain={domain}
                  setDomain={setDomain}
                  port={port}
                  setPort={setPort}
                  codec={codec}
                  setCodec={setCodec}
                  channels={channels}
                  setChannels={setChannels}
                  cps={cps}
                  setCps={setCps}
                  pilotNumber={pilotNumber}
                  setPilotNumber={setPilotNumber}
                  operator={operator}
                  setOperator={setOperator}
                  authUsername={authUsername}
                  setAuthUsername={setAuthUsername}
                  authPassword={authPassword}
                  setAuthPassword={setAuthPassword}
                  authClientUrl={authClientUrl}
                  setAuthClientUrl={setAuthClientUrl}
                  authServerUrl={authServerUrl}
                  setAuthServerUrl={setAuthServerUrl}
                  ipIdentifyBy={ipIdentifyBy}
                  setIpIdentifyBy={setIpIdentifyBy}
                  server={server}
                  setServer={setServer}
                  prefixes={prefixes}
                  setPrefixes={setPrefixes}
                  isInternational={isInternational}
                  setIsInternational={setIsInternational}
                  countryCodes={countryCodes}
                  setCountryCodes={setCountryCodes}
                  handleChange={handleChange}
                  error={error}
                  showAllErrors={showAllErrors}
                />

          <div className="mt-4 flex items-center justify-end gap-2">
            <Button asChild variant="ghost">
              <Link to={`/${ROUTES.TRUNKS.LIST}`}>Cancel</Link>
            </Button>
            <Button type="submit" disabled={buttonDisable}>
              {buttonDisable ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Save className="h-4 w-4" />
              )}
              {buttonDisable ? 'Adding…' : 'Add trunk'}
            </Button>
          </div>
        </form>
      </Card>
    </ScrollPage>
  );
}
